import cx from "classnames";
import { RichTextView } from "components/DesignSystem/RichTextEditor/RichTextView";
import { PreviewBody } from "components/PreviewModal";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { Fragment } from "react";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import { Invoice } from "types/Models/invoice";
import { formatAddressLines } from "util/invoice";
import { currency } from "utils/Currency";
import { InvoicePreviewItems } from "./InvoicePreviewItems";

type InvoicePreivewProps = {
  invoice?: Omit<Invoice, "status" | "uuid">;
  fileId?: string;
  scale?: "normal" | "scaled";
};

const EMPTY_ITEMS: Invoice["invoice_items"] = [];

const SECTION_TITLE_CLASS =
  "t-text-subtext-sm t-font-semibold t-tracking-normal t-text-purple-90";

const formatDisplayDate = (date?: string | null) =>
  date ? dayjs(date).format(DISPLAY_DATE) : "";

export const InvoicePreivew = ({
  invoice,
  fileId,
  scale = "normal",
}: InvoicePreivewProps) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const { data: urlData } = useGetPreviewUrlQuery(
    {
      fileId: invoice?.document?.file_id!,
      groupId,
    },
    {
      skip: !invoice?.document?.file_id || !groupId,
      pollingInterval: 300000,
    }
  );

  const { preview_url } = urlData || {};

  if (!invoice) {
    return null;
  }

  if (fileId) {
    return (
      <div className="t-h-full">
        {preview_url && (
          <PreviewBody
            preview_url={preview_url}
            fullWidth
            file={{
              file_type: "PDF",
              name: `${invoice.invoice_number}.pdf`,
            }}
          />
        )}
      </div>
    );
  }

  const formatAmount = (amount?: string | number | null) =>
    currency({ amount: amount || 0, currencyCode: invoice.currency });

  const { entity, customer } = invoice;
  const companyLines = formatAddressLines(entity?.address);
  const billingLines = formatAddressLines(customer?.billing_address);
  const shippingLines = formatAddressLines(customer?.shipping_address);
  const isSameShipping =
    shippingLines.length === 0 || shippingLines.join() === billingLines.join();
  const billLabel = isSameShipping ? "Bill and ship to:" : "Bill to:";

  const addresses = [{ label: billLabel, lines: billingLines }];
  if (!isSameShipping) {
    addresses.push({ label: "Ship to:", lines: shippingLines });
  }

  const metaRows = [
    { label: "Invoice Date:", value: formatDisplayDate(invoice.invoice_date) },
    { label: "Due Date:", value: formatDisplayDate(invoice.due_date) },
    { label: "PO number:", value: invoice.po_number },
    {
      label: "PO issue date:",
      value: formatDisplayDate(invoice.po_issue_date),
    },
  ].filter((row) => Boolean(row.value));

  const totalRows = [
    { label: "Subtotal:", amount: invoice.sub_total, isVisible: true },
    {
      label: "Discount:",
      amount: invoice.discount_amount,
      isVisible: Number(invoice.discount_amount) > 0,
    },
    {
      label: "Tax:",
      amount: invoice.tax_amount,
      isVisible: Number(invoice.tax_amount) > 0,
    },
    {
      label: "Amount paid:",
      amount: invoice.paid_amount,
      isVisible: Number(invoice.paid_amount) > 0,
    },
  ].filter((row) => row.isVisible);

  const hasPaymentSection = Boolean(
    invoice.payment_instructions || invoice.payment_link
  );

  return (
    <div
      className={cx(
        "t-flex t-flex-col t-gap-[46px] t-bg-white t-rounded t-shadow-light-30 t-px-6 sm:t-px-3 sm:t-gap-8 t-pt-6 t-pb-2 t-flex-1 sm:t-min-w-0 t-text-caption t-text-text-60",
        { "t-[zoom:1.5]": scale === "scaled" }
      )}
    >
      <div className="t-flex t-flex-col t-gap-8">
        <div className="t-hidden sm:t-flex t-justify-between t-items-start t-gap-4">
          {entity?.logo_url && (
            <img
              className="t-max-w-[120px] t-max-h-[40px] t-self-start"
              src={entity.logo_url}
              alt={entity.entity_name}
            />
          )}
          <div className="t-flex t-flex-col t-items-end t-ml-auto">
            <span className="t-text-title-h1 t-tracking-normal">INVOICE</span>
            <span className="t-text-text-30">#{invoice.invoice_number}</span>
          </div>
        </div>
        <div className="t-flex t-justify-between t-items-start t-gap-4">
          <div className="t-flex t-flex-col t-gap-2">
            {entity?.logo_url && (
              <img
                className={cx("t-max-w-[250px] t-self-start sm:t-hidden", {
                  "t-max-h-[54px]": scale === "normal",
                  "t-max-h-[80px]": scale === "scaled",
                })}
                src={entity.logo_url}
                alt={entity.entity_name}
              />
            )}
            <div className="t-flex t-flex-col">
              <span className="t-font-bold">{entity?.entity_name}</span>
              {companyLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </div>
          </div>
          <div className="t-flex t-flex-col t-items-end sm:t-hidden">
            <span className="t-text-title-h1 t-tracking-normal">INVOICE</span>
            <span className="t-text-text-30">#{invoice.invoice_number}</span>
          </div>
        </div>

        <div className="t-flex t-justify-between t-items-start t-gap-4">
          {customer && (
            <div className="t-flex t-gap-5">
              {addresses.map((address) => (
                <div
                  key={address.label}
                  className="t-flex t-flex-col t-gap-0.5 t-w-[158px] sm:t-w-auto"
                >
                  <span className="t-text-neutral-50">{address.label}</span>
                  <div className="t-flex t-flex-col t-text-text-60">
                    {customer.company_name && (
                      <span className="t-font-bold">
                        {customer.company_name}
                      </span>
                    )}
                    {address.lines.map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="t-grid t-grid-cols-[auto_auto] t-gap-x-[9px] t-ml-auto">
            {metaRows.map((row) => (
              <Fragment key={row.label}>
                <span className="t-text-neutral-50 t-whitespace-nowrap t-text-right">
                  {row.label}
                </span>
                <span className="t-min-w-[81px] t-max-w-[98px] t-break-all t-text-right">
                  {row.value}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="t-flex t-flex-col t-gap-9 t-flex-1">
        <div className="t-flex t-flex-col t-gap-4">
          {invoice.title && (
            <span className="t-text-subtext-sm t-tracking-normal">
              {invoice.title}
            </span>
          )}
          <div className="t-flex t-flex-col t-gap-2">
            <InvoicePreviewItems
              items={invoice.invoice_items || EMPTY_ITEMS}
              currencyCode={invoice.currency}
              hasItemLevelTax={invoice.has_item_level_tax}
            />
            <div className="t-flex t-flex-col t-gap-1.5">
              {totalRows.map((row) => (
                <div
                  key={row.label}
                  className="t-flex t-items-center t-justify-end t-gap-[9px]"
                >
                  <span className="t-text-neutral-50">{row.label}</span>
                  <span className="t-w-[134px] t-pr-2.5 t-text-right t-text-text-60">
                    {formatAmount(row.amount)}
                  </span>
                </div>
              ))}
              <div className="t-flex t-items-center t-justify-end t-gap-[9px] t-py-0.5 t-text-subtext t-tracking-normal">
                <span>Amount due:</span>
                <span className="t-w-[132px] t-pr-2.5 t-text-right t-text-text-100">
                  {formatAmount(invoice.due_balance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="t-flex t-flex-col t-gap-7 t-flex-1">
          {invoice.notes && (
            <div className="t-flex t-flex-col t-gap-1.5 t-break-inside-avoid">
              <span className={SECTION_TITLE_CLASS}>Notes</span>
              <RichTextView value={invoice.notes} />
            </div>
          )}
          {hasPaymentSection && (
            <div className="t-flex t-flex-col t-gap-1.5 t-break-inside-avoid">
              <span className={SECTION_TITLE_CLASS}>Payment instructions</span>
              {invoice.payment_instructions && (
                <RichTextView value={invoice.payment_instructions} />
              )}
              {invoice.payment_link && (
                <p className="t-m-0">
                  Payment link (if paying online):{" "}
                  <span className="t-font-bold">{invoice.payment_link}</span>
                </p>
              )}
            </div>
          )}
          {invoice.terms && (
            <div className="t-flex t-flex-col t-gap-1.5 t-break-inside-avoid">
              <span className={SECTION_TITLE_CLASS}>Terms</span>
              <RichTextView value={invoice.terms} />
            </div>
          )}
          {invoice.footer && (
            <div className="t-mt-auto t-border-0 t-border-t t-border-solid t-border-neutral-10 t-pt-2 t-text-center t-italic t-text-[8px] t-leading-[11px] t-text-text-30">
              <RichTextView
                value={invoice.footer}
                className="[&_.ql-editor]:!t-text-center"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
